import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { ArrowRight } from 'lucide-react'

const POST_URL = 'https://uptrack.app/blog/uptrack-in-claude-connector-directory'
const TITLE = 'Ask Claude Which of Your Sites Are Down'
const DESCRIPTION =
  'Uptrack is listed in Claude’s connector directory. Ask Claude which sites are down, check a URL, pause a monitor. Paid plan from $3/mo.'

export const Route = createFileRoute('/_site/blog/uptrack-in-claude-connector-directory')({
  component: ClaudeConnectorDirectoryPage,
  head: () => ({
    meta: [
      { title: `${TITLE} — Uptrack` },
      { name: 'description', content: DESCRIPTION },
      { property: 'og:title', content: `${TITLE} — Uptrack` },
      { property: 'og:description', content: 'Uptrack is listed in Claude’s connector directory. 16 tools, OAuth sign-in, scoped to your organization.' },
      { property: 'og:image', content: 'https://uptrack.app/og/blog-uptrack-in-claude-connector-directory.png' },
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: POST_URL },
      { property: 'article:published_time', content: '2026-10-09' },
    ],
    links: [{ rel: 'canonical', href: POST_URL }],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: `${TITLE}: Uptrack is in Claude’s connector directory`,
          description: DESCRIPTION,
          datePublished: '2026-10-09',
          dateModified: '2026-10-09',
          author: { '@type': 'Organization', name: 'Uptrack' },
          publisher: { '@type': 'Organization', name: 'Uptrack', url: 'https://uptrack.app', logo: { '@type': 'ImageObject', url: 'https://uptrack.app/uptrack-logo-header.png' } },
          mainEntityOfPage: POST_URL,
        }),
      },
    ],
  }),
})

const code = 'rounded bg-muted px-1.5 py-0.5 font-mono text-sm'
const p = 'mb-4 text-lg leading-[1.7] text-body'
const link = 'text-primary hover:underline'
const pre = 'mb-4 overflow-x-auto rounded-md border border-border bg-background p-4 font-mono text-sm text-foreground'

const EXAMPLES: { ask: string; tool: string; result: string }[] = [
  { ask: 'Which of my sites are down?', tool: 'list_monitors, get_dashboard_stats', result: 'Every monitor with its status, plus ongoing incidents and 30-day uptime.' },
  { ask: 'Show uptime for the API over the last 30 days.', tool: 'get_monitor_analytics', result: 'Uptime percentage, daily uptime and response-time percentiles.' },
  { ask: 'Is example.com down right now?', tool: 'check_website', result: 'A check of any public URL from several regions. No monitor needed.' },
  { ask: 'What were the latest failed checks on checkout?', tool: 'get_monitor_checks', result: 'Recent results with status, HTTP code, response time and error.' },
  { ask: 'Pause the staging monitor while I deploy.', tool: 'pause_monitor, resume_monitor', result: 'Pauses it now. Ask Claude to resume it when you finish.' },
  { ask: 'Change the staging monitor to a 5-minute interval.', tool: 'update_monitor', result: 'Updates the name, URL or check interval of one monitor.' },
  { ask: 'Acknowledge the open incident on checkout.', tool: 'list_incidents, acknowledge_incident', result: 'Acknowledges it, which pauses escalation reminders.' },
]

function ClaudeConnectorDirectoryPage() {
  return (
    <article className="article-prose container mx-auto max-w-2xl px-4 py-16">
      <p className="mb-3 text-sm text-muted-foreground">October 9, 2026</p>
      <h1 className="font-sans mb-6 text-4xl font-semibold">Ask Claude which of your sites are down</h1>

      <section className="mb-12">
        <p className={p}>
          Open Claude and type “which of my sites are down?” Claude reads your real monitors and answers.
          Ask it to show uptime for the API over the last 30 days, or to pause the staging monitor while you deploy, and it does that too.
        </p>
        <p className={p}>
          This works because Uptrack is now{' '}
          <a className={link} href="https://claude.ai/directory/uptrack" target="_blank" rel="noopener noreferrer">listed in Claude’s connector directory</a>.
          You find it there and connect it in a few clicks. The connector has 16 tools, and every one is limited to your own organization.
        </p>
        <p className={p}>
          One thing first: the connector needs an Uptrack account on a paid plan. The free plan does not include it.
          Pro costs $3/mo and has a 30-day money-back guarantee. Details are at the end.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">What you can ask</h2>
        <p className={p}>
          Claude picks the tool from your question. You do not name tools. These examples show what happens behind a plain sentence.
        </p>
        <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Example prompts">
          <table className="w-full text-sm">
            <thead className="hidden md:table-header-group">
              <tr className="border-b">
                <th className="pb-3 pr-4 text-left font-medium text-muted-foreground">You ask</th>
                <th className="pb-3 pr-4 text-left font-medium text-muted-foreground">Claude uses</th>
                <th className="pb-3 text-left font-medium text-muted-foreground">You get</th>
              </tr>
            </thead>
            <tbody>
              {EXAMPLES.map((e) => (
                <tr key={e.ask} className="block border-b py-3 md:table-row md:py-0 md:align-top">
                  <td className="block py-1 font-medium text-foreground md:table-cell md:py-3 md:pr-4 md:font-normal md:text-body">{e.ask}</td>
                  <td className="block py-1 md:table-cell md:py-3 md:pr-4"><code className="font-mono text-xs break-words">{e.tool}</code></td>
                  <td className="block py-1 text-body md:table-cell md:py-3">{e.result}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-lg leading-[1.7] text-body">
          The <code className={code}>check_website</code> tool is the same idea as the public{' '}
          <Link to="/tools/is-website-down" className={link}>Is website down? tool</Link>, and it needs no monitor.
          Claude asks you to approve each tool call unless you choose “Always allow”.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">Connect it</h2>
        <h3 className="font-sans mb-2 text-xl font-semibold">Claude on the web, desktop or mobile</h3>
        <ol className="mb-6 list-decimal space-y-2 pl-6 text-lg leading-[1.7] text-body">
          <li>Open <strong>Customize → Connectors</strong> and find <strong>Uptrack</strong> in the directory.</li>
          <li>Choose <strong>Connect</strong>.</li>
          <li>Sign in to Uptrack. On the consent page, check the scopes and press <strong>Allow</strong>.</li>
        </ol>
        <p className={p}>
          Sign-in uses OAuth 2.0 with PKCE. You paste no API key. The consent page lists the scopes before you approve.
        </p>

        <h3 className="font-sans mb-2 text-xl font-semibold">Claude Code</h3>
        <pre tabIndex={0} className={pre}><code>claude mcp add --transport http uptrack https://api.uptrack.app/mcp</code></pre>
        <p className={p}>
          Then run <code className={code}>/mcp</code> inside Claude Code and follow the steps in your browser to sign in.
          This is the flow in the{' '}
          <a className={link} href="https://code.claude.com/docs/en/mcp" target="_blank" rel="noopener noreferrer">Claude Code MCP docs</a>.
        </p>

        <h3 className="font-sans mb-2 text-xl font-semibold">Other clients</h3>
        <p className={p}>
          Any client that supports remote MCP servers and OAuth can use the same URL:{' '}
          <code className={code}>https://api.uptrack.app/mcp</code>. The{' '}
          <Link to="/guides/mcp" className={link}>MCP guide</Link> has the full tool table and scopes.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">The 16 tools</h2>
        <ul className="mb-4 list-disc space-y-3 pl-6 text-lg leading-[1.7] text-body">
          <li>
            <strong>Read-only (9):</strong> <code className={code}>list_monitors</code>, <code className={code}>get_monitor</code>,{' '}
            <code className={code}>get_monitor_analytics</code>, <code className={code}>get_monitor_checks</code>,{' '}
            <code className={code}>get_dashboard_stats</code>, <code className={code}>list_incidents</code>,{' '}
            <code className={code}>list_status_pages</code>, <code className={code}>list_alert_channels</code>, <code className={code}>check_website</code>.
          </li>
          <li>
            <strong>Write (6):</strong> <code className={code}>create_monitor</code>, <code className={code}>update_monitor</code>,{' '}
            <code className={code}>pause_monitor</code>, <code className={code}>resume_monitor</code>,{' '}
            <code className={code}>acknowledge_incident</code>, <code className={code}>create_alert_channel</code> (email, Slack, Discord or Telegram).
          </li>
          <li>
            <strong>Destructive (1):</strong> <code className={code}>delete_monitor</code>.
          </li>
        </ul>
        <p className={p}>
          Every tool has a title and read-only, destructive and idempotent hints. Only <code className={code}>delete_monitor</code> is marked destructive.
          The scopes are <code className={code}>monitors:read</code>, <code className={code}>monitors:write</code>,{' '}
          <code className={code}>incidents:read</code>, <code className={code}>incidents:write</code>,{' '}
          <code className={code}>status_pages:read</code>, <code className={code}>alerts:read</code>,{' '}
          <code className={code}>alerts:write</code> and <code className={code}>analytics:read</code>.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">What Claude cannot do yet</h2>
        <p className={p}>
          The connector does not create status pages, resolve incidents or run browser checks with screenshots.
          Those stay in the dashboard. Claude can list your status pages and acknowledge an incident, but it cannot do more than that today.
          The public <Link to="/tools/browser-check" className={link}>browser check</Link> is a separate page, not a connector tool.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">Security and data</h2>
        <ul className="mb-4 list-disc space-y-3 pl-6 text-lg leading-[1.7] text-body">
          <li>The organization comes from your OAuth token. A tool call cannot read or change another organization.</li>
          <li>A token carries only the scopes you approve. A read-only token cannot call a write tool.</li>
          <li>Uptrack returns only data your account already holds. The server never receives your conversation, only the tool calls Claude makes.</li>
          <li>Email alerts come from Uptrack’s own mail server. No third-party email provider is involved.</li>
          <li>To disconnect, remove the connector in Claude.</li>
        </ul>
        <p className={p}>
          Read the <Link to="/privacy" className={link}>privacy policy</Link> for the full picture.
        </p>
        <p className={p}>
          About the directory label: Uptrack carries the <strong>Community</strong> label. Anthropic screens Community connectors automatically,
          but it has not reviewed them in depth. The listing is not a security audit, and it is not the “Verified” label.
          Read what{' '}
          <a className={link} href="https://claude.com/docs/connectors/verification" target="_blank" rel="noopener noreferrer">each label means</a>{' '}
          and decide for yourself. Check the scopes on the consent page before you press Allow.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">Who this is not for</h2>
        <ul className="mb-4 list-disc space-y-3 pl-6 text-lg leading-[1.7] text-body">
          <li>You do not use Claude or another MCP client. The dashboard and the <Link to="/docs" className={link}>REST API</Link> do the same work.</li>
          <li>You prefer a dashboard-only workflow. A chat adds nothing if you like clicking.</li>
          <li>You want to use the connector on the free plan. The connector needs a paid Uptrack plan, from $3/mo.</li>
          <li>You need to create status pages or resolve incidents from chat. Those are not available yet.</li>
        </ul>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">More on AI and Uptrack</h2>
        <p className={p}>
          The <Link to="/ai" className={link}>AI page</Link> explains how Uptrack works with agents.
          The <Link to="/guides/mcp" className={link}>MCP guide</Link> covers setup and the developer endpoints.
        </p>
      </section>

      <div className="rounded-lg border border-border bg-muted p-8 text-center">
        <h2 className="font-sans mb-3 text-xl font-semibold">Monitor from your chat</h2>
        <p className="mb-6 text-lg leading-[1.7] text-body">
          Pro has 50 monitors at 30-second checks, from $3/mo. If it is not right for you, the{' '}
          <Link to="/refund" className={link}>30-day money-back guarantee</Link> applies.
        </p>
        <Button asChild size="lg">
          <Link to="/pricing">
            See plans <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
          </Link>
        </Button>
      </div>
    </article>
  )
}
